/**
 * Servicio para manejar las cotizaciones
 * 
 * Este servicio integra las funcionalidades de Parse con el flujo de cotizaciones
 * para crear, actualizar y gestionar cotizaciones.
 */

import Parse from '../../config/parse';
import { v4 as uuidv4 } from 'uuid';
import { notifyNewQuotation } from '../email/quotationEmailService';
import { getProductByExactName, getProductByPartialName } from '../product/productService';

export const createQuotation = async (quotationData) => {
  try {
    // Crear un nuevo objeto Quotation
    const Quotation = Parse.Object.extend('Quotation');
    const quotation = new Quotation();

    // Generar un magic link único para esta cotización
    const magicLink = uuidv4();

    // Establecer los datos básicos de la cotización
    quotation.set('userName', quotationData.nombre);
    quotation.set('userEmail', quotationData.email);
    quotation.set('userPhone', quotationData.telefono || '');
    quotation.set('userProfile', quotationData.perfilProfesional || '');
    quotation.set('productType', quotationData.producto || 'Personalizado');
    quotation.set('customFormula', quotationData.formulaUnica || '');
    quotation.set('additionalServices', quotationData.servicios || []);
    quotation.set('status', 'pending'); // Estado inicial: pendiente
    quotation.set('magicLink', magicLink);
    quotation.set('converted', false); // Indica si el usuario ha creado una cuenta

    // ENRICH ITEMS WITH PRICING FROM PRODUCT DB
    let enrichedItems = [];
    if (Array.isArray(quotationData.items)) {
      enrichedItems = quotationData.items.map(item => {
        // Try to find the product by exact or partial name
        let product = getProductByExactName(item.name) || getProductByPartialName(item.name);
        let price = null;
        
        if (!product) {
          // If not found, return item with a warning price
          return { ...item, price: null, priceError: 'Product not found' };
        }
        
        // Find appropriate pricing tier
        if (product.pricing) {
          const tiers = Object.keys(product.pricing)
            .map(qty => parseInt(qty, 10))
            .sort((a, b) => a - b);
          let matchedTier = tiers.find(tierQty => item.quantity >= tierQty);
          // If no tier equal or higher, use the lowest one
          if (!matchedTier) matchedTier = tiers[0];
          price = product.pricing[matchedTier];
        }

        return {
          ...item,
          price: price ?? null,
          moq: product.moq ?? null,
          pricing: product.pricing ?? null,
          countPerBottle: product.countPerBottle ?? null,
          categoryName: product.category ?? null,
          formats: product.formats ?? null
        };
      });
    }
    quotation.set('items', enrichedItems);

    // Guardar el historial de estados
    quotation.set('statusHistory', [{
      status: 'pending',
      date: new Date().toISOString(),
      by: 'system'
    }]);

    // Guardar la cotización en la base de datos
    await quotation.save();
    
    // Enviar email de confirmación con magic link
    await notifyNewQuotation(quotation);
    
    return quotation;
  } catch (error) {
    console.error('Error al crear cotización:', error);
    throw new Error(`Error al crear cotización: ${error.message}`);
  }
};

/**
 * Process a raw quotation item into a standardized format
 * Implements MOQ pricing structure according to MOQ_IMPLEMENTATION_GUIDE.md
 * @param {Object} item - Raw item from Parse
 * @returns {Object} - Processed item with correct types and structure
 */
function processQuotationItem(item) {
  // Ensure we're working with proper types
  const processedItem = {
    id: item.id || '',
    name: item.name || '',
    quantity: Number(item.quantity) || 0,
    price: Number(item.price) || 0,
    moq: Number(item.moq) || 0,
    // Handle both direct and nested pricing structure
    moqOptions: item.moqOptions || (item.pricing ? item.pricing.moqOptions : []) || [],
    price200: Number(item.price200 || (item.pricing ? item.pricing.price200 : 0)) || 0,
    price500: Number(item.price500 || (item.pricing ? item.pricing.price500 : 0)) || 0,
    bulkQuantity: Number(item.bulkQuantity) || Number(item.moq) || 0,
    countPerBottle: Array.isArray(item.countPerBottle) ? item.countPerBottle : [],
    categoryName: item.categoryName || '',
    formats: Array.isArray(item.formats) ? item.formats : [],
    code: item.code || '',
    supplierPercentage: Number(item.supplierPercentage) || 0,
    // Preserve other fields if present
    ...Object.keys(item).reduce((acc, key) => {
      if (!acc[key] && item[key] !== undefined && !['id', 'name', 'quantity', 'price', 'moq', 'moqOptions', 
          'price200', 'price500', 'bulkQuantity', 'countPerBottle', 'categoryName', 'formats', 'code', 
          'supplierPercentage'].includes(key)) {
        acc[key] = item[key];
      }
      return acc;
    }, {})
  };
  
  return processedItem;
}

/**
 * Obtiene una cotización por su ID con datos procesados
 * @param {string} quotationId - ID de la cotización
 * @returns {Promise} - Promesa que se resuelve con la cotización procesada
 */
export const getQuotationById = async (quotationId) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    const parseQuotation = await query.get(quotationId);
    
    // Extract and process items with proper MOQ handling
    const rawItems = parseQuotation.get('items') || [];
    const processedItems = rawItems.map(processQuotationItem);
    
    // Log processed items for debugging
    console.log('Processed items with MOQ data:', processedItems);
    
    // Return the raw Parse object for backward compatibility
    // The component will use the processed items
    parseQuotation._processedItems = processedItems;
    return parseQuotation;
  } catch (error) {
    console.error('Error al obtener cotización:', error);
    throw new Error(`Error al obtener cotización: ${error.message}`);
  }
};
/**
 * Obtiene todas las cotizaciones asociadas a un usuario por su email
 * @param {string} userEmail - Email del usuario
 * @returns {Promise} - Promesa que se resuelve con un array de cotizaciones
 */
export const getQuotationsByUserEmail = async (userEmail) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    
    // Filtrar por el email del usuario
    query.equalTo('userEmail', userEmail);
    
    // Ordenar por fecha de creación (más recientes primero)
    query.descending('createdAt');
    
    // Ejecutar la consulta
    const quotations = await query.find();
    
    return quotations;
  } catch (error) {
    console.error('Error al obtener cotizaciones del usuario:', error);
    throw new Error(`Error al obtener cotizaciones: ${error.message}`);
  }
};

/**
 * Obtiene todas las cotizaciones con opciones de filtrado y paginación
 * @param {Object} options - Opciones de filtrado y paginación
 * @param {string} options.status - Estado de las cotizaciones a filtrar (opcional)
 * @param {string} options.userEmail - Email del usuario para filtrar (opcional)
 * @param {number} options.page - Número de página (comienza en 0)
 * @param {number} options.limit - Número de resultados por página
 * @returns {Promise} - Promesa que se resuelve con un objeto que contiene resultados y total
 */
export const getQuotationsWithPagination = async (options = {}) => {
  try {
    const { status, userEmail, page = 0, limit = 10 } = options;
    
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    
    // Aplicar filtro por estado si se proporciona
    if (status) {
      query.equalTo('status', status);
    }
    
    // Aplicar filtro por email de usuario si se proporciona
    if (userEmail) {
      query.equalTo('userEmail', userEmail);
    }
    
    // Ordenar por fecha de creación (más recientes primero)
    query.descending('createdAt');
    
    // Para usuarios normales, obtenemos todas las cotizaciones del usuario
    // y realizamos la paginación en el cliente para evitar problemas de permisos
    const allResults = await query.find();
    
    // Calcular el total y la paginación
    const total = allResults.length;
    const startIndex = page * limit;
    const endIndex = Math.min(startIndex + limit, total);
    
    // Obtener solo los resultados para la página actual
    const results = allResults.slice(startIndex, endIndex);
    
    return {
      results,
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit)
    };
  } catch (error) {
    console.error('Error al obtener cotizaciones con paginación:', error);
    throw new Error(`Error al obtener cotizaciones: ${error.message}`);
  }
};

/**
 * Actualiza una cotización existente por su ID
 * @param {string} quotationId - ID de la cotización
 * @param {Object} updates - Campos a actualizar (ej. items, additionalNotes)
 * @returns {Promise} - Promesa que se resuelve con la cotización actualizada
 */
export const updateQuotation = async (quotationId, updates) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    const quotation = await query.get(quotationId);

    // Actualizar campos permitidos
    if (updates.items) quotation.set('items', updates.items);
    if (updates.additionalNotes !== undefined) quotation.set('additionalNotes', updates.additionalNotes);
    // Puedes agregar más campos aquí según necesidad

    await quotation.save(null, { useMasterKey: true });
    return quotation;
  } catch (error) {
    console.error('Error al actualizar cotización:', error);
    throw new Error(`Error al actualizar cotización: ${error.message}`);
  }
};

export default {
  createQuotation,
  getQuotationById,
  getQuotationsByUserEmail,
  getQuotationsWithPagination,
  updateQuotation
};
